import type { DbColumn, DbColumnType, DbQueryResult, DbTable } from '@deskworks/shared-types';
import { DB_COLUMN_TYPES } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Columns3, Database, Eye, KeyRound, Pencil, Play, Plug, Plus, Table2, TerminalSquare, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Confirm } from '@/components/confirm';
import { DataApiPanel } from '@/components/data-api';
import { Field } from '@/components/field';
import { Page, PageHeader } from '@/components/page';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { intlLocale, t } from '@/lib/i18n';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

const PAGE = 50;
const key = (workspaceId: string) => ['ws', workspaceId, 'database'] as const;

function formatBytes(n: number | null) {
  if (n == null) return '—';
  return n < 1024 * 1024
    ? `${Math.round(n / 1024).toLocaleString(intlLocale)} KB`
    : `${(n / 1024 / 1024).toLocaleString(intlLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`;
}

function Cell({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground/60 italic">null</span>;
  if (typeof value === 'object') return <span className="font-mono text-[11.5px]">{JSON.stringify(value)}</span>;
  if (typeof value === 'boolean') return <span className="font-mono">{String(value)}</span>;
  const text = String(value);
  return <span title={text.length > 120 ? text : undefined}>{text.length > 120 ? `${text.slice(0, 117)}…` : text}</span>;
}

function ResultTable({ columns, rows }: { columns: string[]; rows: Record<string, unknown>[] }) {
  if (!columns.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[13px]">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            {columns.map((c) => (
              <th key={c} className="px-3 py-2 font-medium whitespace-nowrap">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-muted/30">
              {columns.map((c) => (
                <td key={c} className="max-w-[320px] px-3 py-1.5 align-top">
                  <Cell value={r[c]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <div className="px-3 py-6 text-center text-sm text-muted-foreground">{t('No rows.')}</div>}
    </div>
  );
}

/**
 * Database viewer (docs/features/workspace-database.md): the workspace's own
 * Postgres database — tables, data, columns and a SQL console.
 */
export function DatabasePage() {
  const { workspaceId } = useParams({ from: '/_authed/w/$workspaceId/database' });
  const { table, view } = useSearch({ from: '/_authed/w/$workspaceId/database' });
  const navigate = useNavigate();
  const info = useQuery({ queryKey: [...key(workspaceId), 'info'], queryFn: () => api.database.get(workspaceId) });
  const tables = useQuery({ queryKey: [...key(workspaceId), 'tables'], queryFn: () => api.database.tables(workspaceId), enabled: info.data?.status === 'healthy' });
  const select = (tbl: DbTable | null, v?: 'sql' | 'api') =>
    void navigate({ to: '/w/$workspaceId/database', params: { workspaceId }, search: tbl ? { table: `${tbl.schema}.${tbl.name}` } : { view: v } });
  const selected = table ? tables.data?.find((tbl) => `${tbl.schema}.${tbl.name}` === table) : undefined;

  return (
    <Page wide>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Database className="size-5 text-primary" /> {t('Workspace database')}
          </span>
        }
        description={t("This workspace's own Postgres database. Every desk can use it (read and write, unless a desk is limited to read); agents reach it through its MCP tools.")}
        meta={
          info.data && (
            <>
              <span className="font-mono text-xs">{info.data.name}</span>
              <span>{formatBytes(info.data.sizeBytes)}</span>
              <Badge tone={info.data.status === 'healthy' ? 'green' : info.data.status === 'failing' ? 'red' : 'amber'}>
                {info.data.status === 'healthy' ? t('ready') : info.data.status === 'failing' ? t('failing') : t('being created')}
              </Badge>
              <Link to="/w/$workspaceId/connections/$connectionId" params={{ workspaceId, connectionId: info.data.connectionId }} className="hover:text-foreground hover:underline">
                {t('Desks and permissions')}
              </Link>
            </>
          )
        }
      />
      {info.isPending ? (
        <LoadingRows rows={4} />
      ) : info.isError ? (
        <ErrorState error={info.error} onRetry={() => void info.refetch()} />
      ) : info.data.status !== 'healthy' ? (
        <EmptyState icon={<Database />} title={t("The database isn't ready")} description={info.data.message ?? t('It is being created. Try again in a moment.')} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <Card className="h-fit overflow-hidden">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{t('Tables')}</span>
              <NewTableDialog workspaceId={workspaceId} onCreated={(tbl) => select(tbl)}>
                <Button size="xs" variant="ghost">
                  <Plus /> {t('New')}
                </Button>
              </NewTableDialog>
            </div>
            {tables.isPending ? (
              <LoadingRows rows={3} className="p-3" />
            ) : tables.isError ? (
              <ErrorState error={tables.error} onRetry={() => void tables.refetch()} />
            ) : (
              <div className="py-1">
                {tables.data.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">{t('No tables yet.')}</div>}
                {tables.data.map((tbl) => {
                  const id = `${tbl.schema}.${tbl.name}`;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => select(tbl)}
                      className={cn('flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted/60', table === id && 'bg-muted font-medium')}
                    >
                      {tbl.kind === 'table' ? <Table2 className="size-3.5 text-muted-foreground" /> : <Eye className="size-3.5 text-muted-foreground" />}
                      <span className="min-w-0 flex-1 truncate">{tbl.schema === 'public' ? tbl.name : id}</span>
                      <span className="text-[11px] text-muted-foreground tabular-nums">{tbl.rowEstimate > 0 ? `~${tbl.rowEstimate}` : ''}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              onClick={() => select(null, 'sql')}
              className={cn('flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm hover:bg-muted/60', view === 'sql' && !table && 'bg-muted font-medium')}
            >
              <TerminalSquare className="size-3.5 text-muted-foreground" /> {t('SQL console')}
            </button>
            <button
              type="button"
              onClick={() => select(null, 'api')}
              className={cn('flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm hover:bg-muted/60', view === 'api' && !table && 'bg-muted font-medium')}
            >
              <Plug className="size-3.5 text-muted-foreground" /> {t('Data API')}
            </button>
          </Card>

          {selected ? (
            <TableView workspaceId={workspaceId} table={selected} onDropped={() => select(null)} />
          ) : view === 'sql' ? (
            <SqlConsole workspaceId={workspaceId} />
          ) : view === 'api' ? (
            <DataApiPanel workspaceId={workspaceId} />
          ) : (
            <EmptyState
              icon={<Table2 />}
              title={tables.data?.length ? t('Choose a table') : t('No tables yet')}
              description={t('Create a table here, or let an agent build one. Agents use the same database through its MCP tools.')}
              action={
                <NewTableDialog workspaceId={workspaceId} onCreated={(tbl) => select(tbl)}>
                  <Button>
                    <Plus /> {t('New table')}
                  </Button>
                </NewTableDialog>
              }
            />
          )}
        </div>
      )}
    </Page>
  );
}

function TableView({ workspaceId, table, onDropped }: { workspaceId: string; table: DbTable; onDropped: () => void }) {
  const [tab, setTab] = useState<'data' | 'columns'>('data');
  const [offset, setOffset] = useState(0);
  const data = useQuery({
    queryKey: [...key(workspaceId), 'table', table.schema, table.name, offset],
    queryFn: () => api.database.table(workspaceId, table.schema, table.name, { offset, limit: PAGE }),
  });
  const drop = useApiMutation(() => api.database.dropTable(workspaceId, table.schema, table.name), {
    invalidate: [key(workspaceId)],
    success: t('Table {name} deleted', { name: table.name }),
    onSuccess: onDropped,
  });
  const d = data.data;
  return (
    <Card className="min-w-0 overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <h2 className="min-w-0 flex-1 truncate font-mono text-sm font-semibold">
          {table.schema}.{table.name}
          {table.kind !== 'table' && <span className="ml-2 font-sans text-xs font-normal text-muted-foreground">({table.kind.replace('_', ' ')})</span>}
        </h2>
        <div className="inline-flex rounded-md border p-0.5">
          <Button size="xs" variant={tab === 'data' ? 'secondary' : 'ghost'} onClick={() => setTab('data')}>
            <Table2 /> {t('Data')}
          </Button>
          <Button size="xs" variant={tab === 'columns' ? 'secondary' : 'ghost'} onClick={() => setTab('columns')}>
            <Columns3 /> {t('Columns')}
          </Button>
        </div>
        {table.kind === 'table' && (
          <Confirm
            title={t('Delete table “{name}”?', { name: table.name })}
            description={t('The table and all its rows are deleted. Desks and agents that use it will get errors.')}
            confirmLabel={t('Delete table')}
            destructive
            onConfirm={() => drop.mutateAsync()}
          >
            <Button size="xs" variant="ghost" className="text-destructive hover:text-destructive">
              <Trash2 /> {t('Delete')}
            </Button>
          </Confirm>
        )}
      </div>
      {data.isPending ? (
        <LoadingRows rows={5} className="p-4" />
      ) : data.isError ? (
        <ErrorState error={data.error} onRetry={() => void data.refetch()} />
      ) : tab === 'data' ? (
        <>
          <ResultTable columns={d!.columns.map((c) => c.name)} rows={d!.rows} />
          <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
            <span>
              {d!.total === 0 ? t('No rows') : t('{from}–{to} of {total}', { from: d!.offset + 1, to: d!.offset + d!.rows.length, total: d!.total })}
            </span>
            <div className="flex gap-1">
              <Button size="icon-sm" variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} aria-label={t('Previous rows')}>
                <ChevronLeft />
              </Button>
              <Button size="icon-sm" variant="ghost" disabled={offset + PAGE >= d!.total} onClick={() => setOffset(offset + PAGE)} aria-label={t('Next rows')}>
                <ChevronRight />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <ColumnsView workspaceId={workspaceId} table={table} columns={d!.columns} />
      )}
    </Card>
  );
}

function ColumnsView({ workspaceId, table, columns }: { workspaceId: string; table: DbTable; columns: DbColumn[] }) {
  const editable = table.kind === 'table';
  const invalidate = [key(workspaceId)];
  const drop = useApiMutation((name: string) => api.database.dropColumn(workspaceId, table.schema, table.name, name), { invalidate, success: t('Column deleted') });
  const setNullable = useApiMutation((v: { name: string; nullable: boolean }) => api.database.updateColumn(workspaceId, table.schema, table.name, v.name, { nullable: v.nullable }), {
    invalidate,
  });
  return (
    <div>
      <table className="w-full text-left text-[13px]">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">{t('Column')}</th>
            <th className="px-3 py-2 font-medium">{t('Type')}</th>
            <th className="px-3 py-2 font-medium">{t('Required')}</th>
            <th className="px-3 py-2 font-medium">{t('Default')}</th>
            <th className="w-24 px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {columns.map((c) => (
            <tr key={c.name}>
              <td className="px-3 py-2 font-mono">
                {c.name}
                {c.primaryKey && <KeyRound className="ml-1.5 inline size-3 text-amber-600" aria-label={t('primary key')} />}
              </td>
              <td className="px-3 py-2 font-mono text-xs">{c.type}</td>
              <td className="px-3 py-2">
                <Checkbox
                  checked={!c.nullable}
                  disabled={!editable || c.primaryKey || setNullable.isPending}
                  onChange={(e) => setNullable.mutate({ name: c.name, nullable: !e.target.checked })}
                  aria-label={t('Required')}
                />
              </td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{c.default ?? ''}</td>
              <td className="px-3 py-2 text-right">
                {editable && (
                  <span className="inline-flex gap-0.5">
                    <ColumnDialog workspaceId={workspaceId} table={table} column={c}>
                      <Button size="icon-sm" variant="ghost" aria-label={t('Change {name}', { name: c.name })}>
                        <Pencil />
                      </Button>
                    </ColumnDialog>
                    {!c.primaryKey && (
                      <Confirm
                        title={t('Delete column “{name}”?', { name: c.name })}
                        description={t('The column and its values in every row are deleted.')}
                        confirmLabel={t('Delete column')}
                        destructive
                        onConfirm={() => drop.mutateAsync(c.name)}
                      >
                        <Button size="icon-sm" variant="ghost" className="text-destructive hover:text-destructive" aria-label={t('Delete {name}', { name: c.name })}>
                          <Trash2 />
                        </Button>
                      </Confirm>
                    )}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editable && (
        <div className="border-t px-4 py-3">
          <ColumnDialog workspaceId={workspaceId} table={table}>
            <Button size="sm" variant="outline">
              <Plus /> {t('Add column')}
            </Button>
          </ColumnDialog>
        </div>
      )}
    </div>
  );
}

function TypeSelect({ value, onChange }: { value: string; onChange: (v: DbColumnType) => void }) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as DbColumnType)}>
      <SelectTrigger size="sm">
        <SelectValue placeholder={t('Type')} />
      </SelectTrigger>
      <SelectContent>
        {DB_COLUMN_TYPES.map((type) => (
          <SelectItem key={type} value={type}>
            {type}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Add a column, or rename / retype / change the default of an existing one. */
function ColumnDialog({ workspaceId, table, column, children }: { workspaceId: string; table: DbTable; column?: DbColumn; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const known = (DB_COLUMN_TYPES as readonly string[]).includes(column?.type ?? '') ? (column!.type as DbColumnType) : undefined;
  const [name, setName] = useState(column?.name ?? '');
  const [type, setType] = useState<DbColumnType | undefined>(known ?? (column ? undefined : 'text'));
  const [required, setRequired] = useState(column ? !column.nullable : false);
  const [def, setDef] = useState(column?.default ?? '');
  const save = useApiMutation(
    () =>
      column
        ? api.database.updateColumn(workspaceId, table.schema, table.name, column.name, {
            ...(name !== column.name ? { name } : {}),
            ...(type && type !== column.type ? { type } : {}),
            ...(def !== (column.default ?? '') ? { default: def || null } : {}),
          })
        : api.database.addColumn(workspaceId, table.schema, table.name, { name, type: type ?? 'text', nullable: !required, default: def || null }),
    { invalidate: [key(workspaceId)], success: column ? t('Column changed') : t('Column added'), onSuccess: () => setOpen(false) },
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{column ? t('Change column {name}', { name: column.name }) : t('Add a column to {table}', { table: table.name })}</DialogTitle>
          <DialogDescription>
            {column ? t('Changing the type converts every existing value; it fails if a value doesn’t fit.') : t('Existing rows get the default, or null.')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('Name')} hint={t('lowercase letters, digits and _')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>
          <Field label={t('Type')}>
            <TypeSelect value={type ?? ''} onChange={setType} />
          </Field>
          {!column && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={required} onChange={(e) => setRequired(e.target.checked)} /> {t('Required (not null)')}
            </label>
          )}
          <Field label={t('Default')} optional hint={t('a value, or now(), gen_random_uuid(), true, false')}>
            <Input value={def} onChange={(e) => setDef(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!name.trim()}>
            {column ? t('Save') : t('Add column')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DraftColumn {
  name: string;
  type: DbColumnType;
  required: boolean;
}

function NewTableDialog({ workspaceId, onCreated, children }: { workspaceId: string; onCreated: (table: DbTable) => void; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [cols, setCols] = useState<DraftColumn[]>([{ name: '', type: 'text', required: false }]);
  const save = useApiMutation(
    () =>
      api.database.createTable(workspaceId, {
        name: name.trim(),
        columns: cols.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), type: c.type, nullable: !c.required })),
      }),
    {
      invalidate: [key(workspaceId)],
      success: (created) => t('Table {name} created', { name: created.name }),
      onSuccess: (created) => {
        setOpen(false);
        setName('');
        setCols([{ name: '', type: 'text', required: false }]);
        onCreated({ schema: created.schema, name: created.name, kind: 'table', rowEstimate: 0 });
      },
    },
  );
  const update = (i: number, patch: Partial<DraftColumn>) => setCols(cols.map((c, j) => (i === j ? { ...c, ...patch } : c)));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('New table')}</DialogTitle>
          <DialogDescription>{t('Deskworks adds an id column (a number that counts up) as primary key.')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('Table name')} hint={t('lowercase letters, digits and _, e.g. customer_notes')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>
          <div className="grid gap-2">
            <div className="text-sm font-medium">{t('Columns')}</div>
            {cols.map((c, i) => (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_140px_auto_auto] items-center gap-2">
                <Input value={c.name} placeholder="column_name" onChange={(e) => update(i, { name: e.target.value })} aria-label={t('Column name')} />
                <TypeSelect value={c.type} onChange={(type) => update(i, { type })} />
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Checkbox checked={c.required} onChange={(e) => update(i, { required: e.target.checked })} /> {t('required')}
                </label>
                <Button size="icon-sm" variant="ghost" onClick={() => setCols(cols.filter((_, j) => j !== i))} aria-label={t('Remove column')} disabled={cols.length === 1}>
                  <Trash2 />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="ghost" className="w-fit" onClick={() => setCols([...cols, { name: '', type: 'text', required: false }])}>
              <Plus /> {t('Column')}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!name.trim()}>
            {t('Create table')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SqlConsole({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [sql, setSql] = useState('SELECT now()');
  const [write, setWrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DbQueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.database.query(workspaceId, sql, write ? 'write' : 'read'));
      if (write) void queryClient.invalidateQueries({ queryKey: key(workspaceId) });
    } catch (err) {
      setResult(null);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="min-w-0 overflow-hidden">
      <div className="grid gap-2 border-b p-3">
        <Textarea
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void run();
          }}
          rows={5}
          className="font-mono text-[13px]"
          aria-label="SQL"
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={write}
              onChange={(e) => {
                setWrite(e.target.checked);
                if (e.target.checked) toast.info(t('Write mode'), t('The statement may change data and tables. Writes are recorded in the audit log.'));
              }}
            />
            {t('Allow changes (write mode)')}
          </label>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {t('One statement')} · ⌘↵
            <Button size="sm" onClick={() => void run()} loading={busy} disabled={!sql.trim()}>
              <Play /> {t('Run')}
            </Button>
          </div>
        </div>
      </div>
      {error && <div className="px-4 py-3 text-sm whitespace-pre-wrap text-red-700 dark:text-red-400">{error}</div>}
      {result && (
        <>
          <ResultTable columns={result.columns} rows={result.rows} />
          <div className="border-t px-4 py-2 text-xs text-muted-foreground">
            {result.command} · {result.rowCount === 1 ? t('1 row') : t('{n} rows', { n: result.rowCount ?? 0 })}
            {result.truncated ? ` · ${t('showing the first 500')}` : ''}
          </div>
        </>
      )}
    </Card>
  );
}
